import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, PauseCircle, PlayCircle } from 'lucide-react';

import img from "../Images/heatslide.png";
import img1 from "../Images/water.png";
import img2 from "../Images/valve.png";
import img3 from "../Images/pollution.png";
import img4 from "../Images/power.png";
import img5 from "../Images/chemical.png";
import img6 from "../Images/havac.png";
import img7 from "../Images/solarind.jpg";

const products = [
  {
    title: "Process Heating Solutions",
    description: "High-efficiency steam boilers and industrial heating systems with premium quality standards, ensuring optimal heat transfer and reduced energy consumption for manufacturing processes.",
    tags: ["Steam Generation", "Energy Efficient", "Thermal Efficiency", "Industrial Heating"],
    image: img,
    keywords: "industrial boilers, steam generation, process heating, thermal efficiency"
  },
  {
    title: "Water Treatment Systems",
    description: "Complete water treatment solutions ensuring optimal boiler performance, longevity, and water quality through advanced filtration and purification technologies for industrial applications.",
    tags: ["Water Purification", "Filtration Systems", "Boiler Maintenance", "Water Quality"],
    image: img1,
    keywords: "water treatment, industrial filtration, boiler water treatment, water purification systems"
  },
  {
    title: "Steam Accessories & Components",
    description: "Professional-grade steam system components, including valves, regulators, and pressure controllers, for reliable steam distribution and safe operation in industrial facilities.",
    tags: ["Steam Control", "Pressure Regulation", "System Reliability", "Industrial Components"],
    image: img2,
    keywords: "steam accessories, steam valves, pressure regulators, steam system components"
  },
  {
    title: "Pollution Control Equipment",
    description: "Advanced environmental solutions for reducing emissions, improving air quality, and ensuring compliance with environmental regulations in industrial manufacturing processes.",
    tags: ["Air Pollution Control", "Emission Reduction", "Environmental Compliance", "Sustainability"],
    image: img3,
    keywords: "pollution control, emission control equipment, environmental compliance, air quality systems"
  },
  {
    title: "Power Generation Systems",
    description: "High-efficiency power generation systems utilizing renewable energy sources for sustainable industrial operations and reduced carbon footprint in manufacturing facilities.",
    tags: ["Renewable Energy", "Eco-friendly Power", "Energy Generation", "Sustainable Operations"],
    image: img4,
    keywords: "power generation, renewable energy systems, industrial power, sustainable energy"
  },
  {
    title: "Industrial Chemicals",
    description: "Advanced chemical solutions for water treatment, boiler maintenance, energy efficiency optimization, and improving overall system performance across various industrial applications.",
    tags: ["Water Treatment Chemicals", "Chemical Solutions", "Energy Efficiency", "System Optimization"],
    image: img5,
    keywords: "industrial chemicals, water treatment chemicals, boiler chemicals, system performance"
  },
  {
    title: "HVAC Systems",
    description: "State-of-the-art HVAC systems designed for energy-efficient climate control, ensuring optimal temperature regulation and superior air quality in industrial and commercial settings.",
    tags: ["Climate Control", "Energy Efficiency", "Indoor Air Quality", "Industrial HVAC"],
    image: img6,
    keywords: "industrial HVAC, climate control systems, energy efficient HVAC, commercial air conditioning"
  },
  {
    title: "Solar Energy Solutions",
    description: "Innovative solar power systems providing clean, renewable energy for industrial and residential applications, promoting environmental sustainability and reducing energy costs.",
    tags: ["Renewable Energy", "Solar Power", "Green Energy", "Cost Reduction"],
    image: img7,
    keywords: "solar energy, solar power systems, renewable energy solutions, industrial solar"
  }
];

const ProductSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Preload images for smooth transitions
  useEffect(() => {
    products.forEach(product => {
      const img = new Image();
      img.src = product.image;
    });
  }, []);

  const resetSlideTimer = useCallback(() => {
    setProgress(0);
  }, []);

  const handleSlideChange = useCallback((direction) => {
    if (!isAnimating) {
      setIsAnimating(true);
      resetSlideTimer();
      
      setCurrentSlide(prev => {
        if (direction === 'next') {
          return prev === products.length - 1 ? 0 : prev + 1;
        }
        return prev === 0 ? products.length - 1 : prev - 1;
      });
      
      setTimeout(() => setIsAnimating(false), 750);
    }
  }, [isAnimating, resetSlideTimer]);

  // Auto-advance timer
  useEffect(() => {
    if (isPaused || isHovered) return;

    const timer = setInterval(() => {
      handleSlideChange('next');
    }, 10000);

    const progressInterval = setInterval(() => {
      setProgress(prev => (prev + 1) % 100);
    }, 100);

    return () => {
      clearInterval(timer);
      clearInterval(progressInterval);
    };
  }, [currentSlide, isPaused, isHovered, handleSlideChange]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') handleSlideChange('prev');
      if (e.key === 'ArrowRight') handleSlideChange('next');
      if (e.key === ' ') {
        e.preventDefault();
        setIsPaused(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleSlideChange]);

  // Carousel schema for SEO
  const carouselSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.title,
        "description": product.description,
        "image": product.image
      }
    }))
  };

  const currentProduct = products[currentSlide];

  return (
    <>
      <Helmet>
        {/* Dynamic meta tags based on current slide */}
        <meta name="description" content={`${currentProduct.title} - ${currentProduct.description}`} />
        <meta name="keywords" content={currentProduct.keywords} />
        
        {/* Structured data */}
        <script type="application/ld+json">
          {JSON.stringify(carouselSchema, null, 2)}
        </script>
      </Helmet>

      <section 
        className="relative w-full h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-[90vh] overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="region"
        aria-label="Industrial products and solutions slideshow"
        aria-live="polite"
      >
        <div className="relative w-full h-full">
          {products.map((product, index) => (
            <article
              key={index}
              className={`absolute inset-0 w-full h-full transition-all duration-700 ease-out ${
                index === currentSlide
                  ? 'opacity-100 translate-x-0'
                  : index < currentSlide
                  ? 'opacity-0 -translate-x-full'
                  : 'opacity-0 translate-x-full'
              }`}
              aria-hidden={index !== currentSlide}
            >
              <div className="relative w-full h-full">
                <img
                  src={product.image}
                  alt={`${product.title} - ${product.tags.join(', ')} for industrial applications`}
                  className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] ${
                    index === currentSlide ? 'scale-100' : 'scale-110'
                  }`}
                  loading={index === 0 ? "eager" : "lazy"}
                  width="1920"
                  height="1080"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                  <div className={`absolute bottom-0 left-0 w-full p-4 sm:p-6 md:p-8 lg:p-12 transform transition-all duration-700 ${
                    index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
                  }`}>
                    <div className="max-w-7xl mx-auto">
                      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 md:mb-6 transform transition-all duration-700 delay-100">
                        {product.title}
                      </h2>
                      <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 md:mb-8 max-w-full sm:max-w-2xl md:max-w-3xl transform transition-all duration-700 delay-200 leading-relaxed">
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4">
                        {product.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className={`px-3 py-1 sm:px-4 sm:py-2 md:px-6 md:py-2 text-xs sm:text-sm md:text-base lg:text-lg text-white bg-blue-600/80 rounded-full backdrop-blur-sm
                              transform transition-all duration-500 ${
                                index === currentSlide 
                                  ? 'opacity-100 translate-y-0' 
                                  : 'opacity-0 translate-y-4'
                              }`}
                            style={{
                              transitionDelay: `${200 + tagIndex * 100}ms`
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Progress bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-white/20 z-20" aria-hidden="true">
          <div 
            className="h-full bg-blue-500 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-label="Slideshow progress"
          />
        </div>

        {/* Navigation controls */}
        <div className="absolute inset-0 flex items-center justify-between p-2 sm:p-4 md:p-6 pointer-events-none">
          <button
            onClick={() => handleSlideChange('prev')}
            className="pointer-events-auto p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300 transform hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </button>
          <button
            onClick={() => handleSlideChange('next')}
            className="pointer-events-auto p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300 transform hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </button>
        </div>

        {/* Play/Pause button */}
        <button
          onClick={() => setIsPaused(prev => !prev)}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-300"
          aria-label={isPaused ? "Play slideshow" : "Pause slideshow"}
        >
          {isPaused ? (
            <PlayCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          ) : (
            <PauseCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          )}
        </button>

        {/* Slide indicators */}
        <nav 
          className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex space-x-2 sm:space-x-3 md:space-x-4 z-20"
          aria-label="Slideshow navigation"
        >
          {products.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentSlide(index);
                resetSlideTimer();
              }}
              className={`h-2 sm:h-3 rounded-full transition-all duration-500 ${
                index === currentSlide 
                  ? 'w-8 sm:w-10 md:w-12 bg-blue-500' 
                  : 'w-2 sm:w-3 bg-white/50 hover:bg-white/70'
              }`}
              aria-label={`Go to slide ${index + 1}: ${products[index].title}`}
              aria-current={index === currentSlide}
            />
          ))}
        </nav>
      </section>

      {/* SEO Text Content (Hidden but crawlable) */}
      <div className="sr-only">
        <h1>Industrial Products and Solutions</h1>
        {products.map((product, index) => (
          <div key={index}>
            <h2>{product.title}</h2>
            <p>{product.description}</p>
            <ul>
              {product.tags.map((tag, tagIndex) => (
                <li key={tagIndex}>{tag}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
};

export default ProductSlider;